import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, MoreHorizontal, Eye, Bookmark, Download, Star } from 'lucide-react';
import { exoplanetAPI } from '../services/api';

// Animated cosmic stars background
const AnimatedStars = () => (
  <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
    {[...Array(100)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute rounded-full bg-white opacity-30"
        style={{
          width: `${Math.random() * 2 + 1}px`,
          height: `${Math.random() * 2 + 1}px`,
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
        }}
        animate={{
          opacity: [0.2, 0.5, 0.2],
          y: [0, Math.random() * 20 - 10, 0],
        }}
        transition={{
          duration: Math.random() * 6 + 4,
          repeat: Infinity,
          repeatType: 'loop',
          delay: Math.random() * 4,
        }}
      />
    ))}
  </div>
);

const ExploreDatabase = () => {
  const [activeTab, setActiveTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [exoplanets, setExoplanets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState({
    habitability: '',
    stellarAge: '',
    sortBy: 'discoveryYear'
  });

  const tabs = ['All', 'NASA', 'Kepler'];

  const habitabilityCategories = [
    { name: 'Prime Habitable (80-100%)', value: 'prime', color: 'bg-green-500', selected: selectedFilters.habitability === 'prime' },
    { name: 'Marginal Habitable (60-80%)', value: 'marginal', color: 'bg-yellow-500', selected: selectedFilters.habitability === 'marginal' },
    { name: 'Partially Habitable (30-60%)', value: 'partial', color: 'bg-orange-500', selected: selectedFilters.habitability === 'partial' },
    { name: 'Non-Habitable (0-30%)', value: 'none', color: 'bg-red-500', selected: selectedFilters.habitability === 'none' },
  ];

  const stellarAgeCategories = [
    { name: 'Young Stars (0.1-1 Gyr)', value: 'young', selected: selectedFilters.stellarAge === 'young' },
    { name: 'Main Sequence (1-5 Gyr)', value: 'main', selected: selectedFilters.stellarAge === 'main' },
    { name: 'Mature Stars (5-10 Gyr)', value: 'mature', selected: selectedFilters.stellarAge === 'mature' },
    { name: 'Old Stars (>10 Gyr)', value: 'old', selected: selectedFilters.stellarAge === 'old' },
  ];

  useEffect(() => {
    loadExoplanets();
  }, [activeTab, searchQuery, selectedFilters]);

  const loadExoplanets = async () => {
    setIsLoading(true);
    try {
      const filters = {
        search: searchQuery,
        habitability: selectedFilters.habitability,
        stellarAge: selectedFilters.stellarAge,
        source: activeTab === 'All' ? '' : activeTab
      };

      const response = await exoplanetAPI.getExoplanets(filters);
      setExoplanets(response.planets);
    } catch (error) {
      console.error('Failed to load exoplanets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType] === value ? '' : value
    }));
  };

  const handleSaveExoplanet = async (planet) => {
    try {
      await exoplanetAPI.saveExoplanet(planet);
      // Show success feedback
      console.log('Planet saved successfully');
    } catch (error) {
      console.error('Failed to save planet:', error);
    }
  };

  const resetFilters = () => {
    setSelectedFilters({
      habitability: '',
      stellarAge: '',
      sortBy: 'discoveryYear'
    });
    setSearchQuery('');
  };

  const getHabitabilityColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 30) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getHabitabilityTextColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 30) return 'text-orange-400';
    return 'text-red-400';
  };

  return (
    <div className="flex-1 min-h-screen relative bg-gradient-to-br from-black via-blue-950 to-black overflow-x-hidden">
      <AnimatedStars />
      <div className="flex relative z-10">
        {/* Main Content */}
        <div className="flex-1 p-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <h1 className="text-5xl font-extrabold text-white mb-3 tracking-wide drop-shadow-lg" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>Exoplanet Database</h1>
            <p className="text-lg text-blue-200 font-light tracking-wide">Explore confirmed and candidate exoplanets from NASA's archive</p>
          </motion.div>

          {/* Search and Tabs */}
          <div className="mb-10 flex flex-col gap-6">
            <div className="bg-black/40 backdrop-blur-xl border border-blue-900/40 rounded-2xl p-6 shadow-xl flex items-center gap-4">
              <Search className="w-7 h-7 text-blue-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search exoplanets by name..."
                className="flex-1 bg-transparent text-white placeholder-blue-300 text-xl outline-none font-light tracking-wide"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              />
            </div>
            <div className="flex space-x-8 items-center">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-2 rounded-full font-semibold text-lg transition-all duration-200 shadow-md
                    ${activeTab === tab
                      ? 'bg-gradient-to-r from-blue-700 to-blue-500 text-white shadow-blue-900/40'
                      : 'text-blue-300 hover:text-white hover:bg-blue-900/30'}
                  `}
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  {tab}
                </button>
              ))}
              <div className="ml-auto">
                <select
                  value={selectedFilters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="bg-black/60 border border-blue-900 text-blue-200 rounded-xl px-4 py-2 text-lg shadow-md"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  <option value="discoveryYear">Discovery Date</option>
                  <option value="distance">Distance</option>
                  <option value="habitabilityScore">Habitability</option>
                  <option value="name">Name</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-8">
            <span className="text-blue-300 text-lg font-medium">
              {isLoading ? 'Loading...' : `${exoplanets.length} planets found`}
            </span>
          </div>

          {/* Exoplanet Cards */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-black/40 backdrop-blur-xl border border-blue-900/40 rounded-2xl shadow-xl animate-pulse h-80"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10">
              {exoplanets.map((planet, index) => (
                <motion.div
                  key={planet.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative bg-black/50 backdrop-blur-2xl border border-blue-900/40 rounded-2xl shadow-2xl overflow-hidden hover:border-blue-500/70 transition-all duration-300 group"
                >
                  {/* Animated planet SVG */}
                  <motion.div
                    className="absolute -top-10 -right-10 z-0"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 0.18 }}
                    transition={{ duration: 1.2, delay: 0.2 + index * 0.1, ease: 'easeOut' }}
                  >
                    <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
                      <defs>
                        <radialGradient id={`planetCard${index}`} cx="50%" cy="50%" r="50%">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.95" />
                          <stop offset="60%" stopColor="#1e293b" stopOpacity="0.85" />
                          <stop offset="100%" stopColor="#020617" stopOpacity="1" />
                        </radialGradient>
                      </defs>
                      <circle cx="60" cy="60" r="50" fill={`url(#planetCard${index})`} />
                    </svg>
                  </motion.div>
                  {/* Card Content */}
                  <div className="relative z-10 p-7 flex flex-col gap-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold shadow-md ${getHabitabilityColor(planet.habitabilityScore)}`}>{planet.habitability}</span>
                      <span className="bg-black/60 text-white px-2 py-1 rounded-full text-xs font-mono shadow">{planet.habitabilityScore}%</span>
                    </div>
                    <h3 className="text-white text-2xl font-bold mb-1 tracking-wide drop-shadow" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>{planet.name}</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm text-blue-200 font-light">
                      <div>Distance: <span className="text-white font-medium">{planet.distance}</span></div>
                      <div>Mass: <span className="text-white font-medium">{planet.mass}</span></div>
                      <div>Radius: <span className="text-white font-medium">{planet.radius}</span></div>
                      <div>Period: <span className="text-white font-medium">{planet.orbitalPeriod}</span></div>
                      <div>Temp: <span className="text-white font-medium">{planet.equilibriumTemp}</span></div>
                      <div>Discovered: <span className="text-white font-medium">{planet.discoveryYear}</span></div>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs">
                      <span className="text-blue-400 font-semibold">{planet.source}</span>
                      <span className="text-gray-400">{planet.discoveryMethod}</span>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <button 
                        onClick={() => handleSaveExoplanet(planet)}
                        className="flex-1 bg-gradient-to-r from-blue-700 to-blue-500 hover:from-blue-600 hover:to-blue-400 text-white py-2 px-3 rounded-lg text-sm font-semibold shadow transition-colors flex items-center justify-center gap-1"
                      >
                        <Bookmark className="w-4 h-4" />
                        <span>Save</span>
                      </button>
                      <button className="bg-black/40 hover:bg-blue-900/40 text-white py-2 px-3 rounded-lg text-sm transition-colors flex items-center justify-center shadow">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="bg-black/40 hover:bg-blue-900/40 text-white py-2 px-3 rounded-lg text-sm transition-colors flex items-center justify-center shadow">
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && exoplanets.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-24"
            >
              <Search className="w-20 h-20 text-blue-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-blue-200 mb-2">No exoplanets found</h3>
              <p className="text-blue-300 mb-6">Try adjusting your search criteria or filters</p>
              <button
                onClick={resetFilters}
                className="bg-gradient-to-r from-blue-700 to-blue-500 hover:from-blue-600 hover:to-blue-400 text-white px-8 py-3 rounded-full font-semibold shadow transition-colors"
              >
                Clear Filters
              </button>
            </motion.div>
          )}
        </div>

        {/* Right Sidebar - Filters */}
        <div className="w-96 bg-black/40 backdrop-blur-2xl border-l border-blue-900/40 p-8 rounded-l-3xl shadow-2xl flex flex-col gap-8 mt-10 mb-10 mr-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white text-2xl font-bold tracking-wide">Filters</h3>
            <button 
              onClick={resetFilters}
              className="text-blue-400 hover:text-white text-base font-semibold transition-colors"
            >
              Reset
            </button>
          </div>

          {/* Habitability Filter */}
          <div>
            <h4 className="text-white font-semibold mb-3">Habitability</h4>
            <div className="space-y-4">
              {habitabilityCategories.map((category) => (
                <div key={category.value} className="flex items-center gap-4">
                  <button
                    onClick={() => handleFilterChange('habitability', category.value)}
                    className={`w-5 h-5 rounded-full border-2 transition-colors shadow
                      ${category.selected 
                        ? 'border-blue-500 bg-blue-500 shadow-blue-500/40' 
                        : 'border-blue-300 hover:border-blue-400'}
                    `}
                  ></button>
                  <span className="text-blue-200 text-base flex-1">{category.name}</span>
                  <span className={`w-3 h-3 rounded-full ${category.color}`}></span>
                </div>
              ))}
            </div>
          </div>

          {/* Stellar Age Filter */}
          <div>
            <h4 className="text-white font-semibold mb-3 mt-8">Stellar Age (Gyr)</h4>
            <div className="space-y-4">
              {stellarAgeCategories.map((category, index) => (
                <div key={index} className="flex items-center gap-4">
                  <button
                    onClick={() => handleFilterChange('stellarAge', category.value)}
                    className={`w-5 h-5 rounded-full border-2 transition-colors shadow
                      ${category.selected 
                        ? 'border-blue-500 bg-blue-500 shadow-blue-500/40' 
                        : 'border-blue-300 hover:border-blue-400'}
                    `}
                  ></button>
                  <span className="text-blue-200 text-base">{category.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-black/60 rounded-2xl p-6 mb-4 mt-8 shadow-xl">
            <h4 className="text-white font-semibold mb-4">Database Stats</h4>
            <div className="space-y-3 text-base">
              <div className="flex justify-between">
                <span className="text-blue-200">Total Planets:</span>
                <span className="text-white font-bold">5,234</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-200">Confirmed:</span>
                <span className="text-white font-bold">4,892</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-200">Candidates:</span>
                <span className="text-white font-bold">342</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-200">Potentially Habitable:</span>
                <span className="text-green-400 font-bold">67</span>
              </div>
            </div>
          </div>

          {/* Discovery Timeline Chart */}
          <div className="bg-black/60 rounded-2xl p-6 shadow-xl">
            <h4 className="text-white font-semibold mb-4">Recent Discoveries</h4>
            <div className="space-y-2">
              <div className="flex items-end space-x-2 h-20">
                <div className="w-7 bg-blue-500 h-12 rounded-t"></div>
                <div className="w-7 bg-blue-400 h-16 rounded-t"></div>
                <div className="w-7 bg-blue-600 h-20 rounded-t"></div>
                <div className="w-7 bg-blue-500 h-14 rounded-t"></div>
                <div className="w-7 bg-blue-400 h-18 rounded-t"></div>
              </div>
              <div className="flex justify-between text-sm text-blue-300">
                <span>2020</span>
                <span>2021</span>
                <span>2022</span>
                <span>2023</span>
                <span>2024</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreDatabase; 